import React, { useEffect, useRef, useState, useCallback } from 'react';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import WerkplaatsContactSection from "@/components/WerkplaatsContactSection";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { useCmsContent } from '@/hooks/useCmsContent';

declare global {
  interface Window {
    jQuery: any;
    $: any;
  }
}

const featureKeys = [
  { key: 'collectie_feature_1', default: 'Geselecteerde voertuigen' },
  { key: 'collectie_feature_2', default: 'BOVAG garantie' },
  { key: 'collectie_feature_3', default: 'Uitgebreide keuring' },
  { key: 'collectie_feature_4', default: 'Financiering mogelijk' },
];

export default function OnzeCollectiePage() {
  const { getContent } = useCmsContent('onze-collectie');
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    const tryPlay = () => video.play().catch(() => {});
    tryPlay();
    const forcePlay = () => { tryPlay(); document.removeEventListener('touchstart', forcePlay); };
    document.addEventListener('click', forcePlay, { once: true });
    document.addEventListener('touchstart', forcePlay, { once: true });
    return () => { document.removeEventListener('touchstart', forcePlay); };
  }, []);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const triggerStart = 0;
    const triggerEnd = sectionHeight * 0.6;
    const scrolled = Math.max(0, -rect.top);
    const progress = Math.min(1, Math.max(0, (scrolled - triggerStart) / (triggerEnd - triggerStart)));
    setScrollProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const container = document.getElementById('autodealers-container');
    if (!container) return;

    if (!window.jQuery) {
      const jqueryScript = document.createElement('script');
      jqueryScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
      jqueryScript.onload = () => { loadAutoDeakersContent(); };
      document.head.appendChild(jqueryScript);
    } else {
      loadAutoDeakersContent();
    }

    function loadAutoDeakersContent() {
      const iframe = document.createElement('iframe');
      iframe.id = 'autodealers_frame';
      iframe.src = '//svl.autodealers.nl/occasions.aspx?did=67002&zoek=1';
      iframe.style.width = '100%';
      iframe.style.height = '1000px';
      iframe.style.border = 'none';
      iframe.style.borderRadius = '8px';
      iframe.style.overflow = 'hidden';
      iframe.setAttribute('seamless', 'seamless');
      iframe.setAttribute('data-animation-speed', '2');
      iframe.setAttribute('data-offet-top', '0');
      
      container.innerHTML = '';
      container.appendChild(iframe);

      const receiveMessage = (event: MessageEvent) => {
        if (event.data.id === "ResizeFrame") {
          const height = parseInt(event.data.height);
          if (height > 150) {
            iframe.style.height = (height + 20) + 'px';
          }
        }
      };

      if (window.addEventListener) {
        window.addEventListener("message", receiveMessage);
      } else {
        (window as any).attachEvent("onmessage", receiveMessage);
      }

      (iframe as any)._cleanup = () => {
        if (window.removeEventListener) {
          window.removeEventListener("message", receiveMessage);
        } else {
          (window as any).detachEvent("onmessage", receiveMessage);
        }
      };
    }
    
    return () => {
      const iframe = document.getElementById('autodealers_frame') as any;
      if (iframe && iframe._cleanup) {
        iframe._cleanup();
      }
      if (container) {
        container.innerHTML = `
          <div class="text-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1A06D] mx-auto mb-4"></div>
            <p class="text-neutral-600">Voorraad wordt geladen...</p>
          </div>
        `;
      }
    };
  }, []);

  const basePadding = 16;
  const currentPadding = basePadding * (1 - scrollProgress);
  const currentRadius = 24 * (1 - scrollProgress);
  const contentOpacity = 1 - scrollProgress * 1.5;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section — same style as homepage */}
      <section ref={sectionRef} className="pt-[82px]" style={{ paddingBottom: `${Math.max(0, 8 * (1 - scrollProgress))}px` }}>
        <div
          className="relative w-full mx-auto min-h-[400px] md:min-h-[600px] xl:min-h-[700px] overflow-hidden transition-[border-radius] duration-100"
          style={{
            height: 'calc(100vh - 120px)',
            marginLeft: `${currentPadding}px`,
            marginRight: `${currentPadding}px`,
            width: `calc(100% - ${currentPadding * 2}px)`,
            borderRadius: `${currentRadius}px`,
          }}
        >
          <motion.div
            initial={{ scale: 1.05 }}
            animate={{ scale: 1 }}
            transition={{ duration: 10, ease: 'easeOut' }}
            className="absolute inset-0"
          >
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            >
              <source src="https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/hero-vid.mp4" type="video/mp4" />
            </video>
          </motion.div>

          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

          <div
            className="relative z-20 h-full flex flex-col justify-end pb-8 md:pb-12 lg:pb-16 px-8 md:px-12 lg:px-16"
            style={{ opacity: Math.max(0, contentOpacity) }}
          >
            <div className="max-w-xl">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-6 leading-tight"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {getContent('hero_headline', 'ONZE COLLECTIE')}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-white/90 text-base md:text-lg mb-4 max-w-lg leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {getContent('hero_description', 'Ontdek onze exclusieve selectie van premium voertuigen. Elk voertuig wordt zorgvuldig geselecteerd en gecontroleerd.')}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-white text-lg md:text-xl mb-8"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
Prestatie geeft{' '}
                <span className="relative inline-block">
                  <span className="absolute inset-0 bg-[#B1A06D]" style={{ transform: 'skewX(-4deg) rotate(-2deg)', borderRadius: '4px' }} />
                  <span className="relative z-10 text-black px-2 py-0.5 font-semibold inline-block">
vrijheid
                  </span>
                </span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row sm:items-center gap-6"
              >
                <motion.button
                  onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 bg-white text-black px-6 py-4 rounded-xl font-semibold text-sm uppercase tracking-wider hover:bg-white/95 transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
Bekijk collectie
                </motion.button>
                <div className="flex items-center gap-3 text-white/80 text-sm">
                  <span className="font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
Exclusieve voertuigen
                  </span>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="absolute bottom-8 md:bottom-12 right-8 md:right-12 lg:right-16 z-20 hidden md:block"
            style={{ opacity: Math.max(0, contentOpacity) }}
          >
            <div className="bg-black/70 backdrop-blur-sm rounded-xl px-6 py-4 flex flex-wrap gap-x-8 gap-y-2 items-center">
              {featureKeys.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#B1A06D] flex-shrink-0" />
                  <span className="text-white text-sm font-medium">{getContent(f.key, f.default)}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* AutoDealers Plugin Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white"
          >
            <div className="min-h-[600px]">
              <div id="autodealers-container" className="w-full">
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1A06D] mx-auto mb-4"></div>
                  <p className="text-neutral-600">Voorraad wordt geladen...</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <WerkplaatsContactSection />

      <ReviewsCarousel />

      <Footer />
    </div>
  );
}
